import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import clsx from 'clsx';

export interface TabItem<T extends string> {
  id: T;
  label: ReactNode;
  count?: number;
}

/** WAI-ARIA tablist with arrow-key navigation. Render the panel yourself based on `value`. */
export function Tabs<T extends string>({
  items,
  value,
  onChange,
  label,
}: {
  items: TabItem<T>[];
  value: T;
  onChange: (id: T) => void;
  label: string;
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  const onKey = (e: KeyboardEvent, index: number) => {
    let next = index;
    if (e.key === 'ArrowRight') next = (index + 1) % items.length;
    else if (e.key === 'ArrowLeft') next = (index - 1 + items.length) % items.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = items.length - 1;
    else return;
    e.preventDefault();
    const item = items[next];
    if (item) {
      onChange(item.id);
      refs.current[next]?.focus();
    }
  };

  return (
    // Figma UI kit, Tab Bar (node 1:1771): underline tabs on a bottom border; active = text + 2px accent underline.
    <div role="tablist" aria-label={label} className="flex w-full overflow-x-auto shadow-[inset_0_-1px_0_var(--color-border)]">
      {items.map((item, i) => {
        const selected = item.id === value;
        return (
          <button
            key={item.id}
            ref={(el) => {
              refs.current[i] = el;
            }}
            role="tab"
            type="button"
            id={`tab-${item.id}`}
            aria-selected={selected}
            aria-controls={`panel-${item.id}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(item.id)}
            onKeyDown={(e) => onKey(e, i)}
            className={clsx(
              'inline-flex shrink-0 items-center gap-2 border-b-2 px-6 py-4 text-xs font-semibold tracking-label whitespace-nowrap uppercase transition',
              selected ? 'border-primary text-fg' : 'border-transparent text-fg-muted hover:text-fg-secondary',
            )}
          >
            {item.label}
            {item.count !== undefined && (
              <span
                className={clsx(
                  'rounded-full px-1.5 text-[10px]/[15px] tracking-normal tabular-nums',
                  selected ? 'bg-primary-subtle text-primary' : 'bg-surface-raised text-fg-muted',
                )}
              >
                {item.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function TabPanel({ id, children }: { id: string; children: ReactNode }) {
  return (
    <div role="tabpanel" id={`panel-${id}`} aria-labelledby={`tab-${id}`} className="animate-fade-in">
      {children}
    </div>
  );
}
